<template>
  <div class="schedule-container">
    <div class="schedule-title">
      Mobile Two-Factor Authentication Schedule
    </div>
    <div class="schedule-content">
      <template v-if="fetching">
        <div class="empty-state">
          <loader :size="100" />
        </div>
      </template>
      <template v-else>
        <div
          id="state-message-schedule"
          class="schedule-bar"
        >
          {{ scheduleMessage }}
        </div>
        <template v-if="editScheduleView">
          <div class="schedule-section">
            <div class="schedule-section__title">
              Choose the date range when two-factor will be enforced:
            </div>
            <div class="form-container">
              <label class="form-label">Start Date</label>
              <datetime
                v-model="scheduleInput.startDay"
                input-id="start-date"
                value-zone="local"
                type="date"
                input-class="form-field"
                :week-start="7"
              />
            </div>
            <div class="form-container">
              <label class="form-label">End Date</label>
              <datetime
                v-model="scheduleInput.endDay"
                input-id="end-date"
                value-zone="local"
                type="date"
                input-class="form-field"
                :week-start="0"
              />
            </div>
          </div>
          <div class="schedule-section">
            <div class="schedule-section__title">
              Choose the time range when two-factor will be enforced:
            </div>
            <div class="form-container">
              <label class="form-label">From</label>
              <datetime
                v-model="scheduleInput.startTime"
                input-id="start-time"
                value-zone="local"
                type="time"
                :use12-hour="use12Hour"
                input-class="form-field"
              />
            </div>
            <div class="form-container">
              <label class="form-label">To</label>
              <datetime
                v-model="scheduleInput.endTime"
                input-id="end-time"
                value-zone="local"
                type="time"
                :use12-hour="use12Hour"
                input-class="form-field"
              />
            </div>
          </div>
          <template v-if="loading">
            <loader :size="50" />
          </template>
          <template v-else>
            <div class="schedule-actions--edit">
              <button
                id="cancel-edit-schedule"
                class="btn btn--grey"
                @click="toggleEditSchedule"
              >
                Cancel
              </button>
              <div class="error">
                {{ errorMessage }}
              </div>
              <button
                id="submit-edit-schedule"
                class="btn"
                @click="submitSchedule"
              >
                Set Schedule
              </button>
            </div>
          </template>
        </template>
        <template v-else>
          Choose when you would like to enforce mobile two-factor authentication:
          <div class="schedule-actions">
            <template v-if="loading">
              <loader :size="50" />
            </template>
            <template v-else>
              <div v-tooltip="{ content: updateScheduleTooltip }">
                <button
                  id="always-on-button-schedule"
                  class="btn schedule-actions__button"
                  :disabled="!canUpdateSchedule"
                  @click="enableAlwaysOn"
                >
                  Always On
                </button>
              </div>
              <div v-tooltip="{ content: updateScheduleTooltip }">
                <button
                  id="edit-button-schedule"
                  class="btn schedule-actions__button"
                  :disabled="!canUpdateSchedule"
                  @click="toggleEditSchedule"
                >
                  Set Schedule
                </button>
              </div>
            </template>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import {Datetime} from 'vue-datetime'
import 'vue-datetime/dist/vue-datetime.css'
import Loader from '@/components/loader'
import scheduleQueries from '@/graphql/schedules.js'
import constants from '@/global/constants.js'

const ALWAYS_ON_SCHEDULE = {
  startDay: '1994-04-11T00:00:00Z',
  endDay: '2099-04-11T00:00:00Z',
  startTime: '2019-09-11T04:00:00Z',
  endTime: '2019-09-11T03:59:00Z',
}

export default {
  name: 'ScheduleManagement',
  components: {
    Datetime,
    Loader,
  },
  mixins: [scheduleQueries],
  data() {
    return {
      loading: false,
      fetching: false,
      editScheduleView: false,
      totalRows: 0,
      errorMessage: '',
      paginationParams: {
        page: 1,
        perPage: 10,
      },
      scheduleInput: {},
      schedule: {
        uuid: null,
        startTime: null,
        endTime: null,
      },
      canUpdateSchedule: false,
      use12Hour: true,
    }
  },
  computed: {
    scheduleMessage() {
      const _startDate = moment(this.schedule.startTime).format('LL')
      const _endDate = moment(this.schedule.endTime).format('LL')
      const _startTime = moment(this.schedule.startTime).format('LT')
      const _endTime = moment(this.schedule.endTime).format('LT')
      const _diff = this.getTimeDifferenceHours(_startTime, _endTime)
      if (this.schedule.startTime || this.schedule.endTime) {
        if (this.isAlwaysOnEnabled()) {
          return 'Mobile two-factor authentication is currently scheduled to be enforced at all times.'
        }
        /* eslint-disable max-len*/
        return `Mobile two-factor authentication will be enforced from, ${_startDate} to ${_endDate} between the times of ${_startTime} and ${_endTime} (${_diff}hrs).`
        /* eslint-enable max-len*/
      }
      return 'You need to set up a schedule to receive mobile two-factor requests.'
    },
    updateScheduleTooltip() {
      return this.canUpdateSchedule ? '' : constants.actionNotAvailableMessage
    },
  },
  created() {
    this.canUpdateSchedule = this.$can('update', 'two-factor-schedule')
    this.fetchSchedule()
  },
  methods: {
    async fetchSchedule() {
      this.fetching = true
      try {
        const result = await this.listSchedulesQuery(this.paginationParams)
        const firstSchedule = 0
        const {scheduleList, totalRows} = result
        this.fetching = false
        this.totalRows = totalRows
        if (scheduleList.length > 0) {
          this.schedule = scheduleList[firstSchedule]
        }
      } catch (error) {
        this.fetching = false
        this.displayAlert('fail', 'Unable to fetch schedule.')
        console.log('Fetch Schedules Failed.', error)
      }
    },
    async updateSchedule(schedulePayload) {
      this.loading = true
      try {
        if (this.schedule.uuid) {
          this.schedule = await this.updateScheduleMutation(schedulePayload)
        } else {
          this.schedule = await this.createScheduleMutation(schedulePayload)
        }
        if (this.editScheduleView) {
          this.toggleEditSchedule()
        }
        this.loading = false
        this.displayAlert('success', 'Schedule updated.')
      } catch (error) {
        this.loading = false
        this.displayAlert('fail', 'Unable to update schedule.')
        console.log(error)
      }
    },
    async deleteSchedule() {
      try {
        await this.deleteScheduleMutation(this.schedule.uuid)
        this.displayAlert('success', 'Schedule removed.')
      } catch (error) {
        this.displayAlert('fail', 'Unable to delete schedule.')
      }
    },
    submitSchedule() {
      const _schedulePayload = this.schedulePayload()
      this.checkErrors()
      if (!this.errorMessage) {
        this.updateSchedule(_schedulePayload)
      }
    },
    checkErrors() {
      const {startDay, startTime, endDay, endTime} = this.scheduleInput
      this.errorMessage = ''
      if (!this.checkTimeSameOrBefore(startDay, endDay)) {
        this.errorMessage = 'Please check that the starting date comes before the end date.'
      }
      Object.keys(this.scheduleInput).forEach((input) => {
        if (!this.scheduleInput[input]) {
          this.errorMessage = 'Please fill in all fields.'
        }
      })
    },
    checkTimeSameOrBefore(startDate, endDate) {
      if (startDate && endDate) {
        const _startDate = moment(startDate)
        const _endDate = moment(endDate)
        const _isValid = _startDate.isSameOrBefore(_endDate)
        return _isValid
      }
    },
    toggleEditSchedule() {
      this.editScheduleView = !this.editScheduleView
      this.scheduleInput = {}
    },
    isAlwaysOnEnabled() {
      const {startDay, startTime, endDay, endTime} = ALWAYS_ON_SCHEDULE
      const _alwaysStartTime = this.combineDateTime(startDay, startTime)
      const _alwaysEndTime = this.combineDateTime(endDay, endTime)
      if (
        this.schedule.startTime === _alwaysStartTime
        && this.schedule.endTime === _alwaysEndTime
      ) {
        return true
      }
      return false
    },
    enableAlwaysOn() {
      this.loading = true
      this.scheduleInput = ALWAYS_ON_SCHEDULE
      const _schedulePayload = this.schedulePayload()
      this.updateSchedule(_schedulePayload)
    },
    combineDateTime(date, time) {
      const _date = moment(date)
      const _time = moment(time)
      const timeString = _time.format('LT')
      const dateString = _date.format('L')
      const combined = moment(`${dateString} ${timeString}`)
      return combined.toISOString()
    },
    schedulePayload() {
      const {startDay, startTime, endDay, endTime} = this.scheduleInput
      return {
        uuid: this.schedule.uuid ? this.schedule.uuid : null,
        startTime: this.combineDateTime(startDay, startTime),
        endTime: this.combineDateTime(endDay, endTime),
      }
    },
    getTimeDifferenceHours(startTime, endTime) {
      const _startTime = moment(startTime, 'HH:mm:ss a')
      const _endTime = moment(endTime, 'HH:mm:ss a')
      if (_endTime.isBefore(_startTime)) {
        _endTime.add(1, 'days')
      }
      const _diff = _endTime.diff(_startTime, 'hours')
      if (_diff < 1) {
        return 'less than 1 '
      }
      return _diff
    },
  },
}
</script>

<style lang="scss" scoped>
.schedule {
  &-title {
    font-size: $large-font;
    font-weight: 600;
  }
  &-content {
    @include flex-display();
    @include flex-direction(column);
  }
  &-section {
    margin: 1em 0;
    padding: 1em;
    border: 1px solid $light-grey;
    border-radius: 5px;
    &__title {
      margin-bottom: 1em;
    }
  }
  &-actions {
    margin-top: 1em;
    @include flex-display();
    @include flex-justify-content(flex-start);
    @media #{$mobile} {
      @include flex-justify-content(space-between);
    }
    &--edit {
      @include flex-display();
      @include flex-justify-content(space-between);
    }
    &__button {
      margin-right: 2em;
      @media #{$mobile} {
        margin: 0;
      }
    }
  }
  &-bar {
    background: linear-gradient(to left, $dark-grey, $black);
    padding: 1em 2em;
    border-radius: 5px;
    color: $white;
    min-width: 650px;
    margin: 1em 0;
    text-align: center;
    @media #{$mobile} {
      margin-top: 1em;
      min-width: 200px;
      flex-direction: column;
      align-items: flex-start;
      height: auto;
    }
  }
}
</style>
